import { Component, OnInit, ViewChild, Input, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { ProductSelectorComponent } from '../../common/product-selector/product-selector.component';
import { OrdersFacade } from '../../../+store/order/order.facade';
import { IOrderLineItem, IProductV2 } from '@care-portals/api-interfaces';
import { takeWhile } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { VariantSelectComponent } from '../variant-select/variant-select.component';
import { ProductFacade } from '../../../+store/product/product.facade';

@Component({
  selector: 'portals-emr-order-create',
  templateUrl: './create-order.component.html',
  styleUrls: ['./create-order.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ProductSelectorComponent,
    VariantSelectComponent,
    NzLayoutModule,
    NzSelectModule,
    NzTableModule,
    NzButtonModule,
    NzInputModule
  ]
})
export class OrderCreateComponent implements OnInit, OnDestroy {
  @ViewChild(ProductSelectorComponent, { static: true })
  productSelector: ProductSelectorComponent;

  @Input() mode: 'create' | 'refill';
  @Input() customerId: string;
  @Input() products: IOrderLineItem[];

  isLoading = false;
  isLoadingCategories = false;
  isLoadingProducts = false;
  categories = [];
  category = 0;
  orderForm!: FormGroup;
  error;
  active = true;

  constructor(
    private notification: NzNotificationService,
    private facade: OrdersFacade,
    private productFacade: ProductFacade,
    private formBuilder: FormBuilder,
    private nzDrawerRef: NzDrawerRef
  ) {
    this.orderForm = this.formBuilder.group({
      items: new FormArray([])
    });

    this.facade.loading$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.isLoading = res));

    this.productFacade.loading$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.isLoadingProducts = res));

    this.productFacade.loadingCategories$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.isLoadingCategories = res));
    this.productFacade.categories$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.categories = [{ id: 0, name: 'All' }, ...res]));

    this.facade.orderResult$
      .pipe(takeWhile(() => this.active))
      .subscribe(orderResult => {
        if (orderResult.success) {
          this.nzDrawerRef.close(orderResult.data.id);
          this.facade.clearOrderResult();
        }
      });

    this.facade.error$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => (this.error = res));
  }

  get items(): FormArray {
    return this.orderForm.get('items') as FormArray;
  }

  ngOnInit() {
    this.productFacade.loadCategories();
    this.productFacade.loadProducts();

    if (this.mode === 'refill') {
      this.products.map(product => {
        this.addProduct(
          product.name,
          '' + product.productId,
          '' + product.variantId,
          product.sku,
          product.salePrice,
          product.listPrice,
          product.quantity
        );
      });
    }
  }

  ngOnDestroy(): void {
    this.active = false;
  }

  onProductSelected(product: IProductV2): void {
    //Need to reconfigure to add proper variantId once product options are created
    const defaultVariant =
      product.variants.length > 0
        ? product.variants[product.variants.length - 1]
        : null;
    this.addProduct(
      product.label,
      '' + product._id,
      '' + defaultVariant?.id || product._id,
      defaultVariant?.sku || '',
      defaultVariant?.price || product.price,
      product.listPrice || product.price,
      1
    );
    this.productSelector.clearKeyword();
  }

  addProduct(
    name: string,
    productId: string,
    variantId: string,
    sku: string,
    salePrice: number,
    listPrice: number,
    quantity: number = 1
  ) {
    const existProduct = this.items?.value.filter(
      item => item.productId === productId
    );
    if (existProduct.length > 0) {
      this.notification.info('Information', 'Already exist.');
      return;
    }

    const fgs = this.formBuilder.group({
      name,
      productId: '' + productId,
      variantId: '' + variantId || '',
      sku,
      salePrice: salePrice || 0,
      listPrice: listPrice || 0,
      quantity
    });

    this.orderForm.setControl(
      'items',
      new FormArray([...this.items.controls, fgs])
    );
  }

  removeProduct(index: number): void {
    this.items.removeAt(index);
    this.orderForm.setControl('items', new FormArray([...this.items.controls]));
  }

  clearProducts(): void {
    this.orderForm?.setControl('items', new FormArray([]));
  }

  onVariantChange(itemControl: FormControl, variant) {
    itemControl.get('sku').setValue(variant.sku);
    itemControl.get('variantId').setValue(variant.id);
    itemControl.get('salePrice').setValue(variant.price);
  }

  submit() {
    const orderItemsDTO: IOrderLineItem[] = this.items?.value.map(item => {
      return {
        name: item.name,
        productId: item.productId || 0,
        variantId: item.variantId || 0,
        sku: item.sku,
        salePrice: item.salePrice || 0,
        listPrice: item.listPrice || 0,
        quantity: item.quantity
        // optionId: selectedVariant.option_values[0]?.option_id,
        // optionType: selectedVariant.option_values[0]?.id,
        // optionLabel: selectedVariant.option_values[0]?.label,
        // imageUrl: product.image_url
      } as IOrderLineItem;
    });

    this.facade.createOrder(
      orderItemsDTO,
      this.customerId,
      this.mode === 'refill'
    );
  }
}
